import React, { useState, useMemo, useContext } from "react";
import ReactModal from "react-modal";
import {
  Alert,
  Collapsible,
  FieldGroup,
  Money,
  Separator,
} from "../../components";
import { Button, ButtonGroup } from "../../components/ui";
import { STEPS } from "../constants";
import { AjaxForm } from "../../forms";
import { ListingHelper } from "../../helpers";
import { ListingClassificationFields } from "../../forms/Listing/index.jsx";

import "./internal-details-and-costs.scss";
import Axios from "axios";
import { SectionTrigger } from "./SectionTrigger";
import { useOrderManagement } from "../useOrderManagement";
import { UserContext } from "../../contexts";
import { isActiveStoreOwner } from "../../helpers/adminAccess";
import {
  fxCode,
  getBaseCurrency,
  resolveOriginalMoney,
  useCrossRate,
} from "../../core/currency";
import {
  useListingChangeRequests,
  useRefreshChangeRequests,
  requestListingChange,
  approveListingChange,
  rejectListingChange,
} from "../../hooks/useListingChangeRequests";

// The four handling-cost columns. They are stored in the listing's OWN
// costs_currency; gbp_polishing/gbp_accessories/gbp_shipping/gbp_travel are the
// pivots the backend derives from them (amount × rate(costs_currency → GBP)),
// so they are only ever a fallback for rows that never had a currency.
const OTHER_COST_FIELDS = ["polishing", "accessories", "shipping", "travel"];

// Mirrors ListingChangeRequest::APPROVABLE_FIELDS, which is itself the list
// ListingController strips once orders.costs_finalised is set. The API filters
// again, so this is only about not posting fields nobody can approve.
const APPROVABLE_FIELDS = [
  "private_note",
  "buying_price",
  "buying_currency",
  "supplier",
  "supplier_id",
  "serial_number",
  "costs_currency",
  "polishing",
  "accessories",
  "shipping",
  "travel",
];

export function InternalDetailsAndCosts({
  order_id,
  step,
  lastStep,
  listings,
  fees,
  idx,
  costsCurrentListingIdx,
  saveCosts,
  finaliseCosts,
  marketplaceFee,
  commissionsAmount,
  commissionFees,
  // (listing) => node. Purpose-built flows pass this to swap the SUPPLIER
  // picker + NEW SUPPLIER button for their linked Sell Request slot. Absent on
  // the stock cart flow, which keeps the supplier picker.
  supplierSlot,
}) {
  // fxMissing (FX RULE, hooks/fx.js): om's fee and commission totals were
  // computed without a real rate, so they read "—" like any other missing rate.
  const { isInline, setStep, order, listingRate, fxMissing } = useOrderManagement();
  const { user, accessibleStores, activeStoreId } = useContext(UserContext);
  // Any-currency → any-currency off ONE fetched FX table: the rules of hooks
  // forbid a useFx() per listing row, and this section sums across listings.
  // Every hook in this component runs before the step guard below, so the
  // hook order is identical whether the section renders or not.
  const crossRate = useCrossRate();
  const base = getBaseCurrency();

  // Once costs are finalised, RC master admins still edit directly. Everyone
  // else now goes through an approval: the fields stay OPEN so they can type
  // what they want, but Save becomes "Request change" and nothing is written
  // until the store owner accepts it. `locked` therefore no longer means
  // "read-only" — it means "changes here need approval".
  const needsApproval = !!order?.costs_finalised && !user?.is_master_admin;
  // The store owner reviews; they don't queue requests against themselves.
  // Owner of the ACTIVE store, as the API decides — not the home-store flag.
  const isApprover = isActiveStoreOwner(user, accessibleStores, activeStoreId);
  const locked = needsApproval && isApprover;

  const listingForRequests = listings?.[idx];
  const { data: pendingRequests = [] } = useListingChangeRequests(
    listingForRequests?.id,
    !!order?.costs_finalised
  );
  const refreshChangeRequests = useRefreshChangeRequests();
  const [reviewBusy, setReviewBusy] = useState(null);
  // Hoisted out of AjaxForm's render-prop callback below: a hook inside a
  // child's render function belongs to whichever component happens to invoke
  // it, which is not a contract any child owes its parent. Nothing here depends
  // on the callback's arguments, so the component body is simply where they go.
  const [action, setAction] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [emptyFields, setEmptyFields] = useState(null);

  // Handling costs across the given listings, each converted from ITS OWN
  // costs_currency into the store base. resolveOriginalMoney returns the
  // amount together with the currency it is denominated in, so a row can never
  // be mislabelled; the gbp_* pivot is used only for rows with no currency at
  // all. Returns null when a row's rate is unknown — crossRate's contract is
  // to omit the figure, not to understate it.
  const sumCostsInBase = (rows) => {
    let total = 0;
    for (const l of rows || []) {
      const money = resolveOriginalMoney(l, OTHER_COST_FIELDS, "costs_currency");
      if (!money) continue;
      const rate = crossRate(money.currency, base);
      if (rate == null) return null;
      total += money.amount * rate;
    }
    return total;
  };

  const prevListingsCosts = useMemo(() => {
    if (idx < listings.length - 1) {
      return 0;
    }
    // Each earlier listing may have been costed in a different currency, so
    // they can only be added together once they are all in one.
    return sumCostsInBase(listings.slice(0, idx));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idx, listings, base, crossRate]);

  // Below every hook, deliberately. This section is hidden until the order
  // reaches its step, and `lastStep` advances on a MOUNTED component — so
  // returning early above the hooks changed the hook count between two renders
  // of the same instance, which React treats as a fatal.
  if (lastStep < STEPS.INTERNAL_DETAILS_AND_COSTS) {
    return null;
  }

  const decide = async (id, approve) => {
    setReviewBusy(id);
    try {
      await (approve ? approveListingChange(id) : rejectListingChange(id));
      await refreshChangeRequests();
    } catch (e) {
      console.error("Cost change decision failed", e);
    } finally {
      setReviewBusy(null);
    }
  };

  const listing = listings[idx];
  const initialValues = {
    ...ListingHelper.getInitialValues(listing),
    order_id,
  };
  // The costs slice only — the full listing schema requires eBay Title /
  // Description, which this section neither renders nor needs (see
  // ListingHelper.getCostsValidationSchema).
  const validationSchema = ListingHelper.getCostsValidationSchema();
  const processSubmitValues = ListingHelper.processSubmitValues;

  const formProps = {
    method: "put",
    url: `/api/listings/${listing.id}`,
    initialValues,
    validationSchema,
    enableReinitialize: true,
    excludeFields: ["product.query"],
    submitClassName: "mr-12",
    processSubmitValues,
    showFooter: false,
  };

  return (
    <>
      <Collapsible
        trigger={
          <SectionTrigger
            icon="costs"
            label={`Internal Details & Costs (${idx + 1}/${listings.length})`}
          />
        }
        open={
          step === STEPS.INTERNAL_DETAILS_AND_COSTS &&
          costsCurrentListingIdx === idx
        }
        handleTriggerClick={
          isInline
            ? () =>
                setStep(
                  step === STEPS.INTERNAL_DETAILS_AND_COSTS
                    ? -1
                    : STEPS.INTERNAL_DETAILS_AND_COSTS
                )
            : undefined
        }
      >
        <AjaxForm key={listing.id} {...formProps}>
          {({ validateForm, submitForm, values, resetForm, ...rest }) => {

            const isLastListing = idx + 1 === listings.length;

            const submit = async (actionFromValidate) => {
              const theAction = action || actionFromValidate;

              if (theAction === "save") {
                await saveCosts(submitForm);
              } else if (theAction === "finalize") {
                await finaliseCosts(submitForm);
              } else if (theAction === "request") {
                // Costs are finalised, so nothing is written to the listing.
                // Send only the approvable fields, as typed — the API compares
                // them against the listing as it stands, drops the unchanged
                // ones and stores both sides of whatever is left.
                const proposed = {};
                for (const field of APPROVABLE_FIELDS) {
                  if (values[field] !== undefined) {
                    proposed[field] = values[field];
                  }
                }
                try {
                  await requestListingChange(listing.id, proposed, order_id);
                  await refreshChangeRequests();
                } catch (e) {
                  console.error("Cost change request failed", e);
                }
              }

              setAction(null);
              resetForm();
            };

            const validateForSubmition = async (action) => {
              setAction(action);

              const isValid = await validateForm();
              if (!isValid) {
                return;
              }

              const empty = [];
              if (!values.buying_price) {
                empty.push("buying price");
              }
              if (!values.accessories) {
                empty.push("accessories");
              }
              if (!values.polishing) {
                empty.push("polishing");
              }
              if (!values.shipping) {
                empty.push("shipping");
              }
              if (!values.travel) {
                empty.push("travel");
              }

              if (empty.length > 0) {
                setEmptyFields(empty);
                setShowModal(true);

                return;
              }

              submit(action);
            };

            const confirmSubmition = () => {
              setShowModal(false);
              setEmptyFields(null);
              submit();
            };

            const declineSubmition = () => {
              setShowModal(false);
              setAction(null);
              setEmptyFields(null);
            };

            // Live form values (major units) win over the stored pence columns
            // so the row tracks what the rep is typing; both are denominated in
            // the form's costs_currency, never converted.
            const totalCosts =
              (100 * values.accessories || listing.accessories || 0) +
              (100 * values.polishing || listing.polishing || 0) +
              (100 * values.shipping || listing.shipping || 0) +
              (100 * values.travel || listing.travel || 0);

            // Which currency `totalCosts` is actually in: the form's own select,
            // then the row's stored column, then whatever resolveOriginalMoney
            // can recover — a legacy row holding only gbp_* pivots resolves to
            // GBP and is labelled GBP, instead of wearing the base symbol over
            // an unconverted number.
            const storedCosts = resolveOriginalMoney(
              listing,
              OTHER_COST_FIELDS,
              "costs_currency"
            );
            const totalCostsCurrency =
              fxCode(values.costs_currency || listing.costs_currency || "") ||
              storedCosts?.currency ||
              base;

            // ── Grand total ─────────────────────────────────────────────────
            // INVARIANT: the components are NOT in one currency —
            //   · handling costs → each listing's own costs_currency;
            //   · card / FX fees → the ORDER currency (om converts every
            //     payment and commission fee row into it via useAllFx(
            //     listingRate));
            //   · commissions    → the GBP pivot (om derives commissionsAmount
            //     from the gbp_* itemProfit).
            // Adding them raw produced a number in no currency at all, which
            // was then stamped with the base symbol. Each term is converted
            // into the store base first; if any rate is missing the total is
            // unavailable ("—") rather than wrong.
            //
            // `listingRate` — NOT `order.currency`. The fee terms are whatever
            // om converted them INTO, and om converts with useAllFx(listingRate),
            // exactly as the invariant above says. Reading order.currency first
            // labelled them with a code they are not in whenever the two differ,
            // which is every order still carrying the 'gbp' that migration
            // 2026_06_25_120000 defaulted orders.currency to (see listingRate in
            // useOrderManagement). Same-record rule: take the currency from
            // whatever produced the amount.
            const feesCurrency = listingRate || base;
            const feesToBase = fxMissing ? null : crossRate(feesCurrency, base);
            const gbpToBase = fxMissing ? null : crossRate("gbp", base);
            const costsToBase = crossRate(totalCostsCurrency, base);
            const cardFees = (fees?.card || 0) + (commissionFees?.card || 0);
            const bankFees = (fees?.bank || 0) + (commissionFees?.bank || 0);
            const commissionsInBase =
              gbpToBase == null ? null : (commissionsAmount || 0) * gbpToBase;
            const grandTotalCosts =
              prevListingsCosts == null ||
              costsToBase == null ||
              feesToBase == null ||
              commissionsInBase == null
                ? null
                : prevListingsCosts +
                  totalCosts * costsToBase +
                  (cardFees + bankFees) * feesToBase +
                  commissionsInBase;

            return (
              <div className="internal-details-and-costs mt-4">
                <h4>
                  {listing.product?.brand?.name} {listing.product?.model?.name}
                </h4>

                <ListingClassificationFields
                  listing={listing}
                  disabled={locked}
                  // `locked` = costs are finalised and this rep's changes go
                  // through approval — the slot must not offer writes either.
                  supplierSlot={supplierSlot?.(listing, locked) ?? null}
                />

                <div className="label-value-row mt-4">
                  <div className="strong-label">Total Costs:</div>
                  <div className="value">
                    {/* This listing's OWN figure, in the currency it was
                        entered in — no conversion, so the number the rep typed
                        is the number they see. */}
                    <Money amount={totalCosts} currency={totalCostsCurrency} />
                  </div>
                </div>

                {isLastListing && (
                  <>
                    <Separator />

                    {/* Cross-record aggregates with no recoverable per-record
                        original: commissions exist only as the GBP pivot om
                        computes, and the fee rows have already been summed
                        across payment rows into the ORDER currency. Both are
                        converted into the store base so this block and the
                        grand total below read in one currency; an unavailable
                        rate shows "—" instead of an unconverted figure. */}
                    <div className="strong-label mb-4">External Fees:</div>
                    <div className="row external-fees mb-4">
                      <div className="col-12 col-md-3">
                        TOTAL COMMISSIONS:
                        {commissionsInBase == null ? (
                          " —"
                        ) : (
                          <Money amount={commissionsInBase} currency={base} />
                        )}
                      </div>
                      <div className="col-12 col-md-3">
                        TOTAL CARD FEE:
                        {feesToBase == null ? (
                          " —"
                        ) : (
                          <Money amount={cardFees * feesToBase} currency={base} />
                        )}
                      </div>
                      <div className="col-12 col-md-3">
                        TOTAL FX / OTHER FEE:
                        {feesToBase == null ? (
                          " —"
                        ) : (
                          <Money amount={bankFees * feesToBase} currency={base} />
                        )}
                      </div>
                    </div>

                    <div className="label-value-row mt-5">
                      <div className="strong-label">Grand Total Costs:</div>
                      <div className="value">
                        {grandTotalCosts == null ? (
                          "—"
                        ) : (
                          <Money amount={grandTotalCosts} currency={base} />
                        )}
                      </div>
                    </div>

                    <div className="col-12 col-md-3 hidden">
                      order_id:
                      <Money amount={order_id} />
                    </div>
                  </>
                )}

                {/* Outstanding requests, old → new. Shown to everyone: the
                    submitter sees theirs is queued, and the owner sees what they
                    are being asked to replace — which is why both sides are
                    stored on the request rather than just the new value. */}
                {pendingRequests.length > 0 && (
                  <div className="cost-change-requests mt-4">
                    {pendingRequests.map((cr) => (
                      <div className="cost-change-request" key={cr.id}>
                        <div className="cost-change-request__head">
                          Change requested by {cr.requested_by || "—"}
                          {cr.requested_at ? ` · ${new Date(cr.requested_at).toLocaleDateString()}` : ""}
                        </div>
                        <table className="cost-change-request__table">
                          <tbody>
                            {Object.entries(cr.changes || {}).map(([field, pair]) => (
                              <tr key={field}>
                                <td>{field.replace(/_/g, " ")}</td>
                                <td className="cost-change-request__from">{String(pair?.from ?? "—")}</td>
                                <td aria-hidden="true">→</td>
                                <td className="cost-change-request__to">{String(pair?.to ?? "—")}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {isApprover ? (
                          <div className="cost-change-request__actions">
                            <Button variant="primary" data-btn="cost-confirm-change"
                              loading={reviewBusy === cr.id}
                              onClick={() => decide(cr.id, true)}>
                              Approve
                            </Button>
                            <Button variant="secondary" data-btn="cost-cancel-change"
                              disabled={reviewBusy === cr.id}
                              onClick={() => decide(cr.id, false)}>
                              Reject
                            </Button>
                          </div>
                        ) : (
                          <div className="cost-change-request__waiting">
                            Waiting for the store owner to review.
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <div className="action-buttons text-right mt-5">
                  <Button
                    variant="primary"
                    data-btn="cost-save"
                    disabled={locked}
                    onClick={() => validateForSubmition(needsApproval ? "request" : "save")}
                  >
                    {needsApproval ? "Request change" : "Save & proceed"}
                  </Button>

                  {/* Finalising again is meaningless once costs are finalised —
                      that is the state this whole approval path exists for.

                      It also needs the cart Order: finalising writes
                      `orders.costs_finalised`, and this section now comes
                      BEFORE payments, which is what creates the Order. Without
                      this guard the button would run, write nothing (updateOrder
                      early-returns with no order), navigate on, and leave the
                      step showing incomplete — a silent no-op. Save & Proceed
                      works either way, because the fields it writes live on the
                      listing; only the finalised FLAG needs the Order. */}
                  <Button
                    variant="secondary"
                    data-btn="cost-confirm-final"
                    disabled={
                      idx + 1 < listings.length || locked || needsApproval || !order?.id
                    }
                    title={
                      !order?.id
                        ? "Costs can be finalised once payments have been recorded — that is what creates the order this flag belongs to. Save & proceed keeps everything you have typed."
                        : undefined
                    }
                    onClick={() => validateForSubmition("finalize")}
                  >
                    Finalize cost
                  </Button>
                </div>

                <ReactModal
                  {...{
                    isOpen: showModal,
                    onRequestClose: () => declineSubmition(),
                    portalClassName: "payment-dialog-portal",
                    style: {
                      content: {
                        top: "50%",
                        left: "50%",
                        right: "auto",
                        bottom: "auto",
                        marginRight: "-50%",
                        transform: "translate(-50%, -50%)",
                        borderRadius: "5px",
                        border: "none",
                      },
                    },
                  }}
                >
                  <div className="payment-dialog">
                    <Alert type="warning" message="Confirmation needed">
                      <p>
                        The value of cost is zero for{" "}
                        {emptyFields?.join(", ")?.toUpperCase()}
                      </p>
                    </Alert>
                    {/* ButtonGroup, not a scss gap: .payment-dialog is shared
                        with StepPayments, which still spaces by margins. */}
                    <ButtonGroup align="end" className="action-buttons mt-4">
                      <Button
                        variant="secondary"
                        data-btn="cost-edit-zero"
                        onClick={() => declineSubmition()}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="primary"
                        data-btn="cost-confirm-zero"
                        onClick={() => confirmSubmition()}
                      >
                        Confirm
                      </Button>
                    </ButtonGroup>
                  </div>
                </ReactModal>
              </div>
            );
          }}
        </AjaxForm>
      </Collapsible>
    </>
  );
}
